import { type OpenApiLinkDeliveryServiceType } from '#shared/api/schema';
import { DELIVERY_SERVICES_MAPPING } from '#shared/lib/constants';
import {
    SETTINGS_DELIVERY_CASH_ON_FREE_DELIVERY_ENABLED_SLUG,
    SETTINGS_DELIVERY_CASH_ON_FREE_DELIVERY_SLUG,
    SETTINGS_DELIVERY_CASH_ON_SELLER_SHIPPING_PERCENT_SLUG,
    SETTINGS_DELIVERY_CDEK_ACCOUNT,
    SETTINGS_DELIVERY_CDEK_PASSWORD,
    SETTINGS_DELIVERY_CRATE_ENABLED_SLUG,
    SETTINGS_DELIVERY_DALLI_TOKEN,
    SETTINGS_DELIVERY_DEFAULT_HEIGHT_SLUG,
    SETTINGS_DELIVERY_DEFAULT_LENGTH_SLUG,
    SETTINGS_DELIVERY_DEFAULT_WEIGHT_SLUG,
    SETTINGS_DELIVERY_DEFAULT_WIDTH_SLUG,
    SETTINGS_DELIVERY_DISABLE_ONLINE_PAYMENT_SLUG,
    SETTINGS_DELIVERY_DISABLE_POST_PAYMENT_SLUG,
    SETTINGS_DELIVERY_ENABLE_PARTIAL_REDEMPTION_SLUG,
    SETTINGS_DELIVERY_FREE_DELIVERY_ENABLED_SLUG,
    SETTINGS_DELIVERY_FREE_DELIVERY_SLUG,
    SETTINGS_DELIVERY_HOLDING_PERIOD_DAYS,
    SETTINGS_DELIVERY_ITEM_PER_BOX_ENABLED_SLUG,
    SETTINGS_DELIVERY_ONLINE_PAYMENT_FREE_DELIVERY_ENABLED_SLUG,
    SETTINGS_DELIVERY_ONLINE_PAYMENT_FREE_DELIVERY_SLUG,
    SETTINGS_DELIVERY_ONLINE_PAYMENT_SELLER_SHIPPING_PERCENT_SLUG,
    SETTINGS_DELIVERY_OZON_CUSTOMER_PRICE,
    SETTINGS_DELIVERY_PARTNERS_SLUG,
    SETTINGS_DELIVERY_PECOM_API_KEY,
    SETTINGS_DELIVERY_PECOM_LOGIN,
    SETTINGS_DELIVERY_PECOM_WAREHOUSE_ID,
    SETTINGS_DELIVERY_POST_PAYMENT_MAX_PRICE_SLUG,
    SETTINGS_DELIVERY_PRICE_BASED_ON_PAYMENT_METHOD_SLUG,
    SETTINGS_DELIVERY_RUSSIAN_POST_CODE,
    SETTINGS_DELIVERY_RUSSIAN_POST_SECRET,
    SETTINGS_DELIVERY_RUSSIAN_POST_TOKEN,
    SETTINGS_DELIVERY_SELLER_SHIPPING_PERCENT_SLUG,
    SETTINGS_DELIVERY_SERVICES,
    SETTINGS_DELIVERY_VAT,
    SETTINGS_DELIVERY_YANDEX_TOKEN,
    SETTINGS_DELIVERY_YCP_COURIER_ENABLED,
    SETTINGS_DELIVERY_YCP_PICKUP_POINTS_ENABLED,
    SETTINGS_DELIVERY_YCP_PICKUP_POINTS_LOGO,
    SETTINGS_DELIVERY_YCP_PICKUP_POINTS_NAMING,
    SETTINGS_INTEGRATIONS_ERP_YANDEX_MARKET_CAMPAIGN_ID_SLUG,
} from '../../constants';
import { useSettingsFactory } from './useSettingsFactory';

const crateServices: Array<OpenApiLinkDeliveryServiceType> = [DELIVERY_SERVICES_MAPPING.META_SHIP_PECOM];
const holdingPeriodDaysServices: Array<OpenApiLinkDeliveryServiceType> = [
    DELIVERY_SERVICES_MAPPING.META_SHIP_RUSSIAN_POST,
];

const useDeliveryServiceSettings = (serviceType: OpenApiLinkDeliveryServiceType) =>
    useSettingsFactory({
        freeDelivery: {
            path: SETTINGS_DELIVERY_FREE_DELIVERY_SLUG[`${serviceType}_FREE_DELIVERY_FROM`],
        },
        freeDeliveryEnabled: {
            path: SETTINGS_DELIVERY_FREE_DELIVERY_ENABLED_SLUG[`${serviceType}_FREE_DELIVERY_FROM_ENABLED`],
        },
        sellerShippingPercent: {
            path: SETTINGS_DELIVERY_SELLER_SHIPPING_PERCENT_SLUG[`${serviceType}_SELLER_SHIPPING_COVERAGE_PERCENT`],
        },
        onlinePaymentFreeDelivery: {
            path: SETTINGS_DELIVERY_ONLINE_PAYMENT_FREE_DELIVERY_SLUG[
                `${serviceType}_ONLINE_PAYMENT_FREE_DELIVERY_THRESHOLD`
            ],
        },
        onlinePaymentFreeDeliveryEnabled: {
            path: SETTINGS_DELIVERY_ONLINE_PAYMENT_FREE_DELIVERY_ENABLED_SLUG[
                `${serviceType}_ONLINE_PAYMENT_FREE_DELIVERY_ENABLED`
            ],
        },
        onlinePaymentSellerShippingPercent: {
            path: SETTINGS_DELIVERY_ONLINE_PAYMENT_SELLER_SHIPPING_PERCENT_SLUG[
                `${serviceType}_ONLINE_PAYMENT_SELLER_COVERAGE_PERCENT`
            ],
        },
        cashOnFreeDelivery: {
            path: SETTINGS_DELIVERY_CASH_ON_FREE_DELIVERY_SLUG[
                `${serviceType}_CASH_ON_DELIVERY_FREE_DELIVERY_THRESHOLD`
            ],
        },
        cashOnFreeDeliveryEnabled: {
            path: SETTINGS_DELIVERY_CASH_ON_FREE_DELIVERY_ENABLED_SLUG[
                `${serviceType}_CASH_ON_DELIVERY_FREE_DELIVERY_ENABLED`
            ],
        },
        cashOnSellerShippingPercent: {
            path: SETTINGS_DELIVERY_CASH_ON_SELLER_SHIPPING_PERCENT_SLUG[
                `${serviceType}_CASH_ON_DELIVERY_SELLER_COVERAGE_PERCENT`
            ],
        },
        priceBasedOnPaymentMethod: {
            path: SETTINGS_DELIVERY_PRICE_BASED_ON_PAYMENT_METHOD_SLUG[
                `${serviceType}_DELIVERY_PRICE_BASED_ON_PAYMENT_METHOD`
            ],
        },
        enabledPartners: {
            path: SETTINGS_DELIVERY_PARTNERS_SLUG[`${serviceType}_PARTNERS`],
        },
        ...(crateServices.includes(serviceType)
            ? {
                  crateEnabled: {
                      path: SETTINGS_DELIVERY_CRATE_ENABLED_SLUG[`${serviceType}_CRATE_ENABLED`],
                  },
              }
            : null),
        ...(holdingPeriodDaysServices.includes(serviceType)
            ? {
                  holdingPeriodDays: {
                      path: SETTINGS_DELIVERY_HOLDING_PERIOD_DAYS[`${serviceType}_HOLDING_PERIOD_DAYS`],
                  },
              }
            : null),
    });

export type SettingsDelivery = Array<
    {
        serviceType: OpenApiLinkDeliveryServiceType;
    } & ReturnType<typeof useDeliveryServiceSettings>['values']
>;

export const useSettingsDelivery = () => {
    const { values: additional, refetch } = useSettingsFactory({
        disablePostPayment: { path: SETTINGS_DELIVERY_DISABLE_POST_PAYMENT_SLUG },
        disableOnlinePayment: { path: SETTINGS_DELIVERY_DISABLE_ONLINE_PAYMENT_SLUG },
        postPaymentMaxPrice: { path: SETTINGS_DELIVERY_POST_PAYMENT_MAX_PRICE_SLUG },
        enablePartialRedemption: { path: SETTINGS_DELIVERY_ENABLE_PARTIAL_REDEMPTION_SLUG },
        itemPerBoxEnabled: { path: SETTINGS_DELIVERY_ITEM_PER_BOX_ENABLED_SLUG },
        deliveryServices: { path: SETTINGS_DELIVERY_SERVICES },
        deliveryVat: { path: SETTINGS_DELIVERY_VAT },
        yandexToken: { path: SETTINGS_DELIVERY_YANDEX_TOKEN },
        yandexMarketCampaignId: { path: SETTINGS_INTEGRATIONS_ERP_YANDEX_MARKET_CAMPAIGN_ID_SLUG },
        cdekAccount: { path: SETTINGS_DELIVERY_CDEK_ACCOUNT },
        cdekPassword: { path: SETTINGS_DELIVERY_CDEK_PASSWORD },
        dalliToken: { path: SETTINGS_DELIVERY_DALLI_TOKEN },
        ozonCustomerPrice: { path: SETTINGS_DELIVERY_OZON_CUSTOMER_PRICE },
        russianPostToken: { path: SETTINGS_DELIVERY_RUSSIAN_POST_TOKEN },
        russianPostSecret: { path: SETTINGS_DELIVERY_RUSSIAN_POST_SECRET },
        russianPostCode: { path: SETTINGS_DELIVERY_RUSSIAN_POST_CODE },
        pecomLogin: { path: SETTINGS_DELIVERY_PECOM_LOGIN },
        pecomApiKey: { path: SETTINGS_DELIVERY_PECOM_API_KEY },
        pecomWarehouseId: { path: SETTINGS_DELIVERY_PECOM_WAREHOUSE_ID },
        defaultHeight: { path: SETTINGS_DELIVERY_DEFAULT_HEIGHT_SLUG },
        defaultWidth: { path: SETTINGS_DELIVERY_DEFAULT_WIDTH_SLUG },
        defaultLength: { path: SETTINGS_DELIVERY_DEFAULT_LENGTH_SLUG },
        defaultWeight: { path: SETTINGS_DELIVERY_DEFAULT_WEIGHT_SLUG },
        ycpCourierEnabled: { path: SETTINGS_DELIVERY_YCP_COURIER_ENABLED },
        ycpPickupPointsEnabled: { path: SETTINGS_DELIVERY_YCP_PICKUP_POINTS_ENABLED },
        ycpPickupPointsNaming: { path: SETTINGS_DELIVERY_YCP_PICKUP_POINTS_NAMING },
        ycpPickupPointsLogo: { path: SETTINGS_DELIVERY_YCP_PICKUP_POINTS_LOGO },
    });

    const servicesSettings: Partial<
        Record<OpenApiLinkDeliveryServiceType, ReturnType<typeof useDeliveryServiceSettings>['values']>
    > = {
        [DELIVERY_SERVICES_MAPPING.YANDEX_DELIVERY]: useDeliveryServiceSettings(
            DELIVERY_SERVICES_MAPPING.YANDEX_DELIVERY
        ).values,
        [DELIVERY_SERVICES_MAPPING.YANDEX_MARKET_FBO]: useDeliveryServiceSettings(
            DELIVERY_SERVICES_MAPPING.YANDEX_MARKET_FBO
        ).values,
        [DELIVERY_SERVICES_MAPPING.CDEK]: useDeliveryServiceSettings(DELIVERY_SERVICES_MAPPING.CDEK).values,
        [DELIVERY_SERVICES_MAPPING.META_SHIP_DALLI]: useDeliveryServiceSettings(
            DELIVERY_SERVICES_MAPPING.META_SHIP_DALLI
        ).values,
        [DELIVERY_SERVICES_MAPPING.META_SHIP_PECOM]: useDeliveryServiceSettings(
            DELIVERY_SERVICES_MAPPING.META_SHIP_PECOM
        ).values,
        [DELIVERY_SERVICES_MAPPING.META_SHIP_RUSSIAN_POST]: useDeliveryServiceSettings(
            DELIVERY_SERVICES_MAPPING.META_SHIP_RUSSIAN_POST
        ).values,
    };

    const availableServicesTypes: Array<OpenApiLinkDeliveryServiceType> = [];

    if (additional.yandexToken.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.YANDEX_DELIVERY);
    }

    if (additional.cdekAccount.value && additional.cdekPassword.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.CDEK);
    }

    if (additional.dalliToken.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.META_SHIP_DALLI);
    }

    if (additional.russianPostToken.value && additional.russianPostSecret.value && additional.russianPostCode.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.META_SHIP_RUSSIAN_POST);
    }

    if (additional.pecomLogin.value && additional.pecomApiKey.value && additional.pecomWarehouseId.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.META_SHIP_PECOM);
    }

    if (additional.yandexMarketCampaignId.value) {
        availableServicesTypes.push(DELIVERY_SERVICES_MAPPING.YANDEX_MARKET_FBO);
    }

    /**
     * Включенные и выключенные сервисы доставки.
     * Сервисы доставки для которых есть токены авторизации
     */
    const availableServices = availableServicesTypes.reduce<SettingsDelivery>((acc, serviceType) => {
        if (servicesSettings[serviceType]) {
            return [...acc, { serviceType, ...servicesSettings[serviceType] }];
        }

        return acc;
    }, []);

    /**
     *  Включенные сервисы доставки
     */
    const enableServices = availableServices.filter((service) =>
        additional.deliveryServices.value?.includes(service.serviceType)
    );

    return {
        ...additional,
        enableServices,
        availableServices,
        refetch,
    };
};
